import React, { createContext, useState, useContext, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { appParams } from '@/lib/app-params';
import { createAxiosClient } from '@base44/sdk/dist/utils/axios-client';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [isLoadingPublicSettings, setIsLoadingPublicSettings] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [appPublicSettings, setAppPublicSettings] = useState(null); // Contains only { id, public_settings }

  useEffect(() => {
    checkAppState();
  }, []);

  const checkAppState = async () => {
    try {
      setIsLoadingPublicSettings(true);
      setAuthError(null);
      
      // First, check app public settings (with token if available)
      // This will tell us if auth is required, user not registered, etc.
      const buildAppClient = (token) => createAxiosClient({
        baseURL: `/api/apps/public`,
        headers: {
          'X-App-Id': appParams.appId
        },
        token: token || undefined,
        interceptResponses: true
      });

      let effectiveToken = appParams.token;

      try {
        let publicSettings;
        try {
          publicSettings = await buildAppClient(effectiveToken).get(`/prod/public-settings/by-id/${appParams.appId}`);
        } catch (settingsError) {
          // A 403 auth_required while a token was sent means the stored
          // token is stale/invalid. Drop it and retry as a guest — otherwise
          // a public app bounces to login and back forever in a reload loop.
          if (effectiveToken && settingsError.status === 403 && settingsError.data?.extra_data?.reason === 'auth_required') {
            try { localStorage.removeItem('base44_access_token'); localStorage.removeItem('token'); } catch (_e) { /* best-effort token clear */ }
            effectiveToken = null;
            publicSettings = await buildAppClient(null).get(`/prod/public-settings/by-id/${appParams.appId}`);
          } else {
            throw settingsError;
          }
        }
        setAppPublicSettings(publicSettings);

        // If we got the app public settings successfully, check if user is authenticated
        if (effectiveToken) {
          await checkUserAuth();
        } else {
          setIsLoadingAuth(false);
          setIsAuthenticated(false);
          setAuthChecked(true);
        }
        setIsLoadingPublicSettings(false);
      } catch (appError) {
        console.error('App state check failed:', appError);
        
        // Handle app-level errors
        if (appError.status === 403 && appError.data?.extra_data?.reason) {
          const reason = appError.data.extra_data.reason;
          if (reason === 'auth_required') {
            setAuthError({
              type: 'auth_required',
              message: 'Authentication required'
            });
          } else if (reason === 'user_not_registered') {
            setAuthError({
              type: 'user_not_registered',
              message: 'User not registered for this app'
            });
          } else {
            setAuthError({
              type: reason,
              message: appError.message
            });
          }
        } else {
          setAuthError({
            type: 'unknown',
            message: appError.message || 'Failed to load app'
          });
        }
        setIsLoadingPublicSettings(false);
        setIsLoadingAuth(false);
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      setAuthError({
        type: 'unknown',
        message: error.message || 'An unexpected error occurred'
      });
      setIsLoadingPublicSettings(false);
      setIsLoadingAuth(false);
    }
  };

  const checkUserAuth = async () => {
    try {
      // Now check if the user is authenticated
      setIsLoadingAuth(true);
      const currentUser = await base44.auth.me();
      setUser(currentUser);
      setIsAuthenticated(true);
      setIsLoadingAuth(false);
      setAuthChecked(true);
      // On signup / login, immediately try to link this user to their
      // ConsumerAccount record so they appear in the live customer view
      // without waiting for the daily catch-up sweep. Best-effort & silent.
      if (currentUser && currentUser.role !== 'admin') {
        try { await base44.functions.invoke('linkMyConsumerAccount', {}); } catch (_e) { /* best-effort */ }
      }
    } catch (error) {
      // A 401/403 here is an expected stale-token case on a public app — we
      // clear it silently below. Only surface genuinely unexpected errors so
      // the console isn't noisy with periodic me() auth failures.
      if (!(error?.status === 401 || error?.status === 403)) {
        console.error('User auth check failed:', error);
      }
      setIsLoadingAuth(false);
      setIsAuthenticated(false);
      setAuthChecked(true);

      // A failed me() here means the stored access token is stale/invalid.
      // We only reach this point once the public-settings fetch already
      // succeeded, so the app is accessible. Do NOT trigger a login redirect
      // — on a public app that would loop forever (login → back → stale
      // token → me() fails → login → …). Just drop the bad token and render
      // as a guest. A genuinely private app requests login via the
      // public-settings 403 path instead.
      if (error.status === 401 || error.status === 403) {
        try { localStorage.removeItem('base44_access_token'); localStorage.removeItem('token'); } catch (_e) { /* best-effort token clear */ }
      }
    }
  };

  // Lightweight re-fetch of the current user without toggling the loading
  // flags. Used to keep the cached user (and role) in sync after server-side
  // changes such as an admin promotion, invite claim, or demotion.
  const refreshUser = async () => {
    try {
      const currentUser = await base44.auth.me();
      setUser(currentUser);
      setIsAuthenticated(true);
    } catch (_e) {
      // keep existing state — refresh is best-effort
    }
  };

  const logout = (shouldRedirect = true) => {
    setUser(null);
    setIsAuthenticated(false);
    
    if (shouldRedirect) {
      // Use the SDK's logout method which handles token cleanup and redirect
      base44.auth.logout(window.location.href);
    } else {
      // Just remove the token without redirect
      base44.auth.logout();
    }
  };

  const navigateToLogin = () => {
    // Use the SDK's redirectToLogin method
    base44.auth.redirectToLogin(window.location.href);
  };

  // Keep the current user's role in sync when their User record changes
  // server-side (admin promotion, invite claim, demotion) and when they
  // return to the tab — so admin UI reflects the new role without a re-login.
  useEffect(() => {
    if (!user?.id) return;
    let mounted = true;
    const handler = (event) => {
      if (event?.id && event.id !== user.id) return;
      if (mounted) refreshUser();
    };
    let unsubscribe;
    try { unsubscribe = base44.entities.User.subscribe(handler); } catch (_e) { /* subscribe unavailable */ }
    const onFocus = () => { if (mounted) refreshUser(); };
    window.addEventListener('focus', onFocus);
    return () => {
      mounted = false;
      if (typeof unsubscribe === 'function') unsubscribe();
      window.removeEventListener('focus', onFocus);
    };
  }, [user?.id]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated, 
      isLoadingAuth,
      isLoadingPublicSettings,
      authError,
      appPublicSettings,
      authChecked,
      logout,
      navigateToLogin,
      checkUserAuth,
      checkAppState,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};